import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  buildMonitoringImageKey,
  buildMonitoringVideoKey,
} from './creator-monitor-add-asset.helpers';
import {
  AttachPublishedContentAssetType,
  AttachPublishedContentAssetsDto,
} from '@/app/common/general-types/social-screening.dto';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'cmol-creator-monitor-add-asset',
  imports: [],
  templateUrl: './creator-monitor-add-asset.html',
  styleUrl: './creator-monitor-add-asset.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorMonitorAddAsset implements OnDestroy {
  @ViewChild('addAssetDialog', { static: false }) addAssetDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('addAssetButton', { static: false }) addAssetButton?: ElementRef<HTMLButtonElement>;

  publishedContentId = input.required<string>();
  inquiryId = input.required<string>();

  assetsAttached = output<void>();

  assetType = signal<AttachPublishedContentAssetType>('images');
  selectedImageFiles = signal<File[]>([]);
  selectedVideoFile = signal<File | null>(null);
  imagePreviewUrls = signal<string[]>([]);
  videoPreviewUrl = signal<string | null>(null);
  isDragging = signal(false);
  saving = signal(false);
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';

  subscriptionList = new Subscription();

  private readonly s3UploaderService = inject(S3uploaderService);
  readonly s3Uploader = this.s3UploaderService;
  private readonly socialMonitoring = inject(SocialMonitoring);
  private readonly toast = inject(ToastNotificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  setAssetType(type: AttachPublishedContentAssetType): void {
    if (this.saving()) {
      return;
    }
    this.assetType.set(type);
    this.clearSelections();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }
    this.applyFiles(files);
  }

  onFileInputChange(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl.files?.length) {
      this.applyFiles(inputEl.files);
    }
    inputEl.value = '';
  }

  removeImageAt(index: number): void {
    const next = [...this.selectedImageFiles()];
    next.splice(index, 1);
    this.revokeImagePreviews();
    this.selectedImageFiles.set(next);
    this.imagePreviewUrls.set(next.map((f) => URL.createObjectURL(f)));
  }

  clearVideo(): void {
    this.revokeVideoPreview();
    this.selectedVideoFile.set(null);
    this.videoPreviewUrl.set(null);
  }

  canSave(): boolean {
    if (this.saving()) {
      return false;
    }
    if (this.assetType() === 'images') {
      return this.selectedImageFiles().length > 0;
    }
    return this.selectedVideoFile() !== null;
  }

  async saveAssets(): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    this.saving.set(true);
    this.loadingState = 'loading';
    this.refreshView();

    const contentId = this.publishedContentId();

    try {
      if (this.assetType() === 'images') {
        const uploadedImages: { s3_url: string; s3_key: string }[] = [];
        const files = this.selectedImageFiles();

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const key = buildMonitoringImageKey(contentId, i + 1, file.type);
          const uploaded = await this.s3Uploader.uploadMonitoringImage(file, key);
          if (!uploaded) {
            throw new Error(`Failed to upload image slide ${i + 1}`);
          }
          uploadedImages.push({ s3_url: uploaded.fullUrl, s3_key: uploaded.key });
        }

        const dto: AttachPublishedContentAssetsDto = {
          assetType: 'images',
          publishedContentId: contentId,
          inquiryId: this.inquiryId(),
          images: uploadedImages,
        };

        await this.attachAssets(dto);
      } else {
        const file = this.selectedVideoFile();
        if (!file) {
          throw new Error('No video selected');
        }
        const key = buildMonitoringVideoKey(contentId, file.type);
        const uploaded = await this.s3Uploader.uploadMonitoringVideo(file, key);
        if (!uploaded) {
          throw new Error('Failed to upload video');
        }

        const dto: AttachPublishedContentAssetsDto = {
          assetType: 'video',
          publishedContentId: contentId,
          inquiryId: this.inquiryId(),
          video: { s3_url: uploaded.fullUrl, s3_key: uploaded.key },
        };

        await this.attachAssets(dto);
      }

      this.loadingState = 'loaded';
      this.saving.set(false);
      this.clearSelections();
      this.closeDrawer();
      this.assetsAttached.emit();
      void this.toast.alert({
        title: 'Assets saved',
        message: 'Published content assets were uploaded successfully.',
        type: 'success',
        icon: 'success',
      });
      this.refreshView();
    } catch (err: unknown) {
      console.error('saveAssets failed:', err);
      this.loadingState = 'error';
      this.saving.set(false);
      void this.toast.alert({
        title: 'Upload failed',
        message: err instanceof Error ? err.message : 'Could not save assets. Please try again.',
        type: 'error',
        icon: 'error',
      });
      this.refreshView();
    }
  }

  closeDrawer(): void {
    this.addAssetDialog?.nativeElement.close();
  }

  ngOnDestroy(): void {
    this.clearSelections();
    this.subscriptionList.unsubscribe();
  }

  private attachAssets(dto: AttachPublishedContentAssetsDto): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.subscriptionList.add(
        this.socialMonitoring.attachPublishedContentAssets(dto).subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err),
        }),
      );
    });
  }

  private applyFiles(fileList: FileList): void {
    if (this.assetType() === 'images') {
      const images: File[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);
        if (file?.type.startsWith('image/')) {
          images.push(file);
        }
      }
      if (!images.length) {
        return;
      }
      const merged = [...this.selectedImageFiles(), ...images];
      this.revokeImagePreviews();
      this.selectedImageFiles.set(merged);
      this.imagePreviewUrls.set(merged.map((f) => URL.createObjectURL(f)));
    } else {
      let video: File | null = null;
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);
        if (file?.type.startsWith('video/')) {
          video = file;
          break;
        }
      }
      if (!video) {
        return;
      }
      this.revokeVideoPreview();
      this.selectedVideoFile.set(video);
      this.videoPreviewUrl.set(URL.createObjectURL(video));
    }
    this.refreshView();
  }

  private clearSelections(): void {
    this.revokeImagePreviews();
    this.revokeVideoPreview();
    this.selectedImageFiles.set([]);
    this.imagePreviewUrls.set([]);
    this.selectedVideoFile.set(null);
    this.videoPreviewUrl.set(null);
  }

  private revokeImagePreviews(): void {
    for (const url of this.imagePreviewUrls()) {
      URL.revokeObjectURL(url);
    }
  }

  private revokeVideoPreview(): void {
    const url = this.videoPreviewUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
}
